import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Login.css";
import { getStoredData } from "../../api/fetchData.jsx";
import { getAllPossiblePermissions } from "../../utils/permissions.js";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const Login = () => {
  const [username, setUsername] = useState(
    localStorage.getItem("savedEmail") || "",
  );
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(
    localStorage.getItem("rememberMe") === "true",
  );
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const data = getStoredData();
  const fields = data?.[2] || {};
  const stored = getStoredData();
  const baseApi = stored?.[0]?.Api || "https://lttsbackend.axiomos.in";

  // AUTO CLEAR EXPIRED SESSION
  useEffect(() => {
    const expirationTime = localStorage.getItem("expirationTime");
    if (expirationTime && new Date().getTime() > expirationTime) {
      localStorage.clear();
    }
  }, []);

  const findMissingPermissions = (userPerms) => {
    const allPermissions = getAllPossiblePermissions();
    const uniquePerms = [...new Set(allPermissions)];
    return uniquePerms.filter((perm) => !userPerms.includes(perm));
  };

  const fetchAppVersion = async () => {
    try {
      const res = await axios.get("https://lttsbackend.axiomos.in/apk/latest");
      return res.data?.version || null;
    } catch (err) {
      console.error("❌ Failed to fetch app version:", err);
      return null;
    }
  };

  const handleLogin = async () => {
    try {
      console.log("🔄 Logging in…");

      const appVersion = await fetchAppVersion();
      localStorage.setItem("appVersion", appVersion || "unknown");

      // LOGIN
      const response = await axios.post(
        `${baseApi}/user/login`,
        {
          emailId: username,
          password,
          appVersion,
        },
        { headers: { "Content-Type": "application/json" } },
      );

      if (response.data.token && response.data.user) {
        const { token, user } = response.data;

        const allowedRoles = [
          "Admin",
          "Client",
          "Project Manager",
          "Management",
        ];
        const roleName = user?.role?.roleName;

        if (!allowedRoles.includes(roleName)) {
          setError("⛔ You do not have permission to access this panel.");
          return;
        }

        // Remember Me Logic
        const expirationDuration = rememberMe
          ? 7 * 24 * 60 * 60 * 1000 // 7 days
          : 24 * 60 * 60 * 1000; // 24 hours

        localStorage.setItem("authenticated", "true");
        localStorage.setItem("token", token);
        localStorage.setItem(
          "expirationTime",
          new Date().getTime() + expirationDuration,
        );

        // Save email only if Remember Me is checked
        if (rememberMe) {
          localStorage.setItem("savedEmail", username);
        } else {
          localStorage.removeItem("savedEmail");
        }

        localStorage.setItem("rememberMe", rememberMe);

        const {
          _id,
          userId,
          name,
          emailId,
          phone,
          address,
          designation,
          role,
          project = [],
          isActive,
          isApproved,
          photo,
          createdAt,
          updatedAt,
        } = user;

        const userDetailsToStore = {
          _id,
          userId,
          name,
          emailId,
          phone,
          address,
          designation: designation || null,

          // populated role → always save clean structure
          role: role?._id
            ? {
                _id: role._id,
                roleName: role.roleName,
                permissions: role.permissions || [],
              }
            : role || null,

          // project array → always stored as clean array
          project: Array.isArray(project)
            ? project.map((p) => ({
                _id: p._id,
                projectName: p.projectName,
                company: p.company || null,
                description: p.description || null,
                siteLocation: p.siteLocation || null,
                projectId: p.projectId || null,
                status: p.status || null,
                startDate: p.startDate || null,
                endDate: p.endDate || null,
              }))
            : [],

          // app level flags
          isActive: isActive !== false,
          isApproved: !!isApproved,

          // photo url from backend (safe)
          photo: photo || null,

          // meta
          createdAt,
          updatedAt,
        };

        // store safely
        localStorage.setItem("userDetails", JSON.stringify(userDetailsToStore));

        // Save permissions
        localStorage.setItem(
          "permissions",
          JSON.stringify(role?.permissions || []),
        );
        localStorage.setItem(
          "fieldsAccess",
          JSON.stringify(role?.fieldsAccess || []),
        );

        navigate("/");
      } else {
        setError(
          fields?.errorMessagesinvalidCredentials || "Invalid credentials",
        );
      }
    } catch (err) {
      console.error("❌ Login Error:", err);

      setError(
        err.response?.data?.message ||
          fields?.errorMessagesinvalidCredentials ||
          "Invalid credentials",
      );
    }
  };

  return (
    <div className="login-page auth-theme">
      <div className="login-container">
        <div className="login-section">
          <img
            src={fields?.headerlogo || "/imagesLogin/lttslogo.png"}
            alt="Logo"
            className="login-logo"
          />

          <h2 className="login-heading">
            {fields?.headerwelcomeMessage || "Welcome Back"}
          </h2>
          <p className="login-subheading">
            {fields?.headersubheading ||
              "Enter To View Your Platform's Performance"}
          </p>

          {/* EMAIL */}
          <div className="form-group">
            <label className="form-label">
              {fields?.formemailLabel || "Email"}
            </label>
            <input
              type="text"
              className="form-input-login"
              placeholder={fields?.formemailPlaceholder || "Enter your email"}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          {/* PASSWORD with SHOW/HIDE TOGGLE */}
          <div className="form-group" style={{ position: "relative" }}>
            <label className="form-label">
              {fields?.formpasswordLabel || "Password"}
            </label>

            <input
              type={showPassword ? "text" : "password"}
              className="form-input-login"
              placeholder={fields?.formpasswordPlaceholder || "Enter password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <FontAwesomeIcon
              icon={showPassword ? faEyeSlash : faEye}
              onClick={() => setShowPassword(!showPassword)}
              className="password-toggle-icon"
            />
          </div>

          <div className="login-BelowLinks">
            {/* REMEMBER ME */}
            <div className="remember-me">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                {fields?.formrememberMeLabel || "Remember Me"}
              </label>
            </div>

            {/* FORGOT PASSWORD */}
            <p
              className="forgot-password"
              onClick={() => navigate("/reset-password")}
              style={{ cursor: "pointer", color: "var(--color-primary)" }}
            >
              {fields?.formforgotPasswordText || "Forgot Your Password?"}
            </p>
          </div>

          <button className="login-button" onClick={handleLogin}>
            {fields?.buttonslogin || "Login"}
          </button>

          {error && <p className="error">{error}</p>}

          <p className="register-link">
            {fields?.linksregisterText || "Don't have an account?"}{" "}
            <a
              href={fields?.linksregisterHref || "/register"}
              className="register-link-text"
            >
              {fields?.linksregisterLinkText || "Register Here"}
            </a>
          </p>
        </div>
      </div>

      <div className="design-side">
        <div
          className="slide"
          style={{
            backgroundImage:
              "url(/imagesLogin/1761298262348-A10_PJS_9699.webp)",
            animationDelay: "0s",
          }}
        />
        <div
          className="slide"
          style={{
            backgroundImage: "url(/imagesLogin/1761644455253-DJI_0028.webp)",
            animationDelay: "7.5s",
          }}
        />
        <div
          className="slide"
          style={{
            backgroundImage: "url(/imagesLogin/1761744580051-ALWAR_ESIC.webp)",
            animationDelay: "15s",
          }}
        />
        <div
          className="slide"
          style={{
            backgroundImage: "url(/imagesLogin/1761801048078-DSC_0232.webp)",
            animationDelay: "22.5s",
          }}
        />
      </div>
    </div>
  );
};

export default Login;
